import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, Users, MessageSquare, Calendar, Eye, Target } from 'lucide-react';
import { useEngagement } from '@/hooks/useEngagement';

const EngagementROIWidget = () => {
  const { currentMonth, computeScore, isLoading } = useEngagement();

  const score = computeScore(currentMonth);
  const stats = currentMonth;

  const metrics = [
    { label: 'Connexions', value: stats?.connections_made || 0, icon: Users, color: 'text-blue-600' },
    { label: 'Messages', value: stats?.messages_sent || 0, icon: MessageSquare, color: 'text-green-600' },
    { label: 'Événements', value: stats?.events_attended || 0, icon: Calendar, color: 'text-purple-600' },
    { label: 'Vues reçues', value: stats?.profile_views_received || 0, icon: Eye, color: 'text-orange-600' },
    { label: 'Opportunités', value: stats?.opportunities_viewed || 0, icon: Target, color: 'text-rose-600' },
  ];

  const getScoreLabel = (s: number) => {
    if (s >= 80) return { text: 'Excellent', color: 'text-green-600' };
    if (s >= 50) return { text: 'Bon', color: 'text-blue-600' };
    if (s >= 20) return { text: 'À développer', color: 'text-orange-600' };
    return { text: 'Débutant', color: 'text-muted-foreground' };
  };

  const scoreLabel = getScoreLabel(score);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="h-5 bg-muted rounded w-40 animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const monthName = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          Ma valeur ce mois
        </CardTitle>
        <p className="text-xs text-muted-foreground capitalize">{monthName}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Score global */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Score d'engagement</span>
            <span className={`font-semibold ${scoreLabel.color}`}>
              {score}/100 — {scoreLabel.text}
            </span>
          </div>
          <Progress value={score} className="h-2" />
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.label} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40">
                <Icon className={`h-3.5 w-3.5 ${metric.color} flex-shrink-0`} />
                <div>
                  <div className="text-sm font-semibold">{metric.value}</div>
                  <div className="text-[10px] text-muted-foreground">{metric.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {score === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">
            Commencez à interagir pour voir votre score évoluer !
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default EngagementROIWidget;
